import type React from "react";

const minGap = 10;

type Item = {
  elem: HTMLLIElement;
  innerText: string;
  isFit: boolean;
  total: number;
  totalWithGaps: number;
  w: number;
};

type Sum = {
  items: Item[];
  lastFitIndex: number;
  total: number;
  totalWithGaps: number;
};

export const getListItemsData = (
  listItems: HTMLLIElement[],
  listWidth: number,
): Sum | unknown => {
  return [].reduce.call(
    listItems,
    (sum, elem, index) => {
      const { innerText } = elem as HTMLLIElement;
      const { width: elemWidth } = (
        elem as HTMLLIElement
      ).getBoundingClientRect();
      const total = (sum as Sum).total + elemWidth;
      const totalWithGaps =
        (sum as Sum).items.length > 0
          ? (sum as Sum).items[index - 1].totalWithGaps + elemWidth + minGap
          : elemWidth + minGap;
      const isFit = totalWithGaps <= listWidth;
      const itemInfo = {
        w: elemWidth,
        innerText,
        elem,
        total,
        totalWithGaps,
        isFit,
      };
      (sum as Sum).items.push(itemInfo);
      (sum as Sum).total = total;
      (sum as Sum).totalWithGaps = totalWithGaps;
      (sum as Sum).lastFitIndex = isFit ? index : (sum as Sum).lastFitIndex;

      return sum;
    },
    { total: 0, items: [] },
  );
};

export const getNewGap = (listItemsData: Sum, listWidth: number) => {
  const { lastFitIndex, items } = listItemsData;
  if (!lastFitIndex || !items[lastFitIndex]) {
    return { difference: 0, newGap: minGap };
  }

  const { total } = items[lastFitIndex];
  const diff = Math.abs(listWidth - items[lastFitIndex].total);
  let newGap = Math.floor(diff / lastFitIndex) + 100;
  const getNewGaps = () => newGap * lastFitIndex;
  while (getNewGaps() + total >= listWidth) {
    newGap -= 1;
  }

  const difference = listWidth - total - getNewGaps();

  return { difference, newGap };
};

export const adjustLinkGaps = (
  isMobile: boolean,
  listRef: React.RefObject<HTMLUListElement | null>,
) => {
  if (isMobile) {
    return;
  }
  const list = listRef.current;
  if (!list) {
    return;
  }

  const { childNodes: listItems } = list;

  const { width: listWidth } = list.getBoundingClientRect();
  const listItemsData = getListItemsData(
    Array.from(listItems) as HTMLLIElement[],
    listWidth,
  ) as Sum;

  const { difference, newGap } = getNewGap(listItemsData, listWidth);

  list.style.columnGap = `${newGap}px`;

  for (let i = 0; i < listItems.length; i += 1) {
    if (i < difference - 1) {
      (listItems[i] as HTMLLIElement).style.marginRight = "1px";
    } else {
      (listItems[i] as HTMLLIElement).style.marginRight = "";
    }
  }
};
